import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { clamp, formatSemesterDateRanges } from 'Util/myPath';
import { useConfig } from 'Context/ConfigContext';
import { Flex, Text, MUIModal, Box, ButtonFilled, Checkbox, SvgIcon } from 'Components/Base';
import ProductProgressCard from 'Components/ProductProgressCard';
import useMigrateMPProducts from 'Routes/MyCourses/Domain/useMigrateMPProducts';

import { mapProductProgressesToProductProgressCards } from '../../MyPathProgressTracker/utils';

const EmptyStateWrapper = styled(Flex)(({ theme }) => ({
  height: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.colors.myPath.gray[50],
  borderRadius: `${theme.radii[2]}px`,
}));

const ProductProgressCardWrapper = styled(Flex)(({ theme }) => ({
  overflow: 'auto',
  maxHeight: '320px',
  height: 'fit-content',
  flexDirection: 'column',
  gridRowGap: theme.space[2],
  overflowY: 'auto',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const StyledButtonFilled = styled((props) => <ButtonFilled {...props} />)`
  ${({ theme }) => css`
    &:disabled {
      background-color: ${theme.colors.myPath.gray[200]};
      color: ${theme.colors.myPath.gray[500]};
      border-color: ${theme.colors.myPath.gray[200]};
    }
  `}
`;

const SeeLastSemesterBlockProductsModal = ({
  isOpen,
  onCloseModal,
  mpPreviousMyProgressTracker,
  onRefetch,
  ...props
}) => {
  const [selectedStudentProductTimelineIds, setSelectedStudentProductTimelineIds] = useState([]);
  const { myPath } = useConfig();
  const { t } = useTranslation('myPath');
  const { mutate: migrateMPProducts, loading } = useMigrateMPProducts({
    onCompleted: () => {
      onRefetch();
      onCloseModal();
    },
  });

  const handleCheckboxChange = (productId) => {
    setSelectedStudentProductTimelineIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(productId)) {
        return prevSelectedIds.filter((id) => id !== productId);
      } else {
        return [...prevSelectedIds, productId];
      }
    });
  };

  const hasSelectedStudentProductTimelineIds = selectedStudentProductTimelineIds.length > 0;

  const handleMigrateMPProduct = () => {
    migrateMPProducts({
      variables: {
        studentProductTimelineIds: selectedStudentProductTimelineIds,
      },
    });
  };

  const { products, semesterStartDate, semesterEndDate } = useMemo(() => {
    if (!mpPreviousMyProgressTracker) {
      return { products: [], semesterStartDate: null, semesterEndDate: null };
    }
    return {
      products: mapProductProgressesToProductProgressCards(
        mpPreviousMyProgressTracker.productProgresses,
        mpPreviousMyProgressTracker.studentSemester,
        myPath.imageUrl,
        myPath.prefixImageUrl
      ).sort(
        (a, b) =>
          (clamp(a.percentComplete) === 100 || a.isSplitted) -
          (clamp(b.percentComplete) === 100 || b.isSplitted)
      ),
      semesterStartDate: mpPreviousMyProgressTracker.studentSemester.startDate,
      semesterEndDate: mpPreviousMyProgressTracker.studentSemester.endDate,
    };
  }, [mpPreviousMyProgressTracker, myPath.imageUrl, myPath.prefixImageUrl]);

  const hasPreviousSemester =
    mpPreviousMyProgressTracker?.studentSemester?.startDate &&
    mpPreviousMyProgressTracker?.studentSemester?.endDate;

  const semesterDateRanges = hasPreviousSemester
    ? formatSemesterDateRanges(semesterStartDate, semesterEndDate)
    : '';

  return (
    <MUIModal
      disableBackdropClick
      isOpen={isOpen}
      onCloseModal={onCloseModal}
      modalStyle={{ borderRadius: '16px', maxWidth: '1080px' }}
      innerProps={{ height: '100%' }}
      modalTitle={<Text textStyle={'heading4'}>แผนเทอมที่แล้ว</Text>}
      modalTitleProps={{ disableTypography: true }}
      styleModalContentProps={{ padding: 0 }}
      {...props}
    >
      <>
        <Box padding={6}>
          <Text textStyle={'caption'} color={'myPath.textColor.secondary'} mb={4}>
            {semesterDateRanges}
          </Text>
          <Flex
            flexDirection={'column'}
            overflow={'auto'}
            flexGrow={1}
            gridRowGap={4}
            height="560px"
          >
            <>
              {products.length > 0 ? (
                <ProductProgressCardWrapper style={{ maxHeight: '100%' }}>
                  {products.map((productProgress) => {
                    const disabledCheckbox =
                      clamp(productProgress.percentComplete) === 100 || productProgress.isSplitted;
                    const description = productProgress.isSplitted
                      ? { text: '(เพิ่มเข้าเทอมปัจจุบันแล้ว)', color: 'myPath.success.500' }
                      : '';
                    const showEncouragementText =
                      !productProgress.isSplitted && clamp(productProgress.percentComplete) === 100;

                    return (
                      <Flex
                        key={productProgress.id}
                        alignItems="center"
                        gridColumnGap={2}
                        onClick={() => {
                          if (disabledCheckbox) {
                            return;
                          }
                          handleCheckboxChange(productProgress.id);
                        }}
                      >
                        {disabledCheckbox ? (
                          <SvgIcon
                            icon={SvgIcon.ICON_LIST.CHECK_CIRCLE_OUTLINE}
                            size={20}
                            color={'myPath.success.500'}
                            style={{ flexShrink: 0 }}
                          />
                        ) : (
                          <Checkbox
                            checked={selectedStudentProductTimelineIds.includes(productProgress.id)}
                            size={18}
                            style={{ flexShrink: 0, padding: 0 }}
                          />
                        )}
                        <ProductProgressCard
                          name={productProgress.name}
                          subjectName={productProgress.subjectName}
                          totalDuration={productProgress.totalDuration}
                          percentComplete={productProgress.percentComplete}
                          productType={productProgress.productType}
                          thumbnailUrl={productProgress.thumbnailUrl}
                          hasProduct={productProgress.hasProduct}
                          milestones={productProgress.milestones}
                          encouragementType={productProgress.encouragementType}
                          hideMilestone={productProgress.isSplitted}
                          showEncouragementText={showEncouragementText}
                          description={description}
                          style={{ flexGrow: 1 }}
                        />
                      </Flex>
                    );
                  })}
                </ProductProgressCardWrapper>
              ) : (
                <EmptyStateWrapper>
                  <Text
                    textStyle={'subtitle1'}
                    fontWeight={'500'}
                    color={'myPath.textColor.disabled'}
                  >
                    ไม่มีแผนการเรียนที่วางไว้
                  </Text>
                </EmptyStateWrapper>
              )}
            </>
          </Flex>
        </Box>
        <Box
          bottom={0}
          padding={6}
          backgroundColor={'white'}
          style={{ boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)' }}
        >
          <StyledButtonFilled
            width={1}
            disabled={!hasSelectedStudentProductTimelineIds || loading}
            onClick={handleMigrateMPProduct}
          >
            <Text textStyle={'body2'} fontWeight={'600'}>
              {`${t('MIGRATE_BUTTON')} ${
                hasSelectedStudentProductTimelineIds
                  ? `(${selectedStudentProductTimelineIds.length})`
                  : ''
              }`}
            </Text>
          </StyledButtonFilled>
        </Box>
      </>
    </MUIModal>
  );
};

SeeLastSemesterBlockProductsModal.propTypes = {
  isOpen: PropTypes.bool,
  onCloseModal: PropTypes.func,
  mpPreviousMyProgressTracker: PropTypes.object,
  onRefetch: PropTypes.func,
};

export default SeeLastSemesterBlockProductsModal;
